/* eslint-disable no-console */
import { UseQueryResult } from 'react-query'

import { useRouter } from 'next/router'
import { useDispatch } from 'react-redux'
import { setSalesforceDetails } from 'state/booking/actions'
import useAppSelector from 'hooks/useAppSelector'
import { useEffect } from 'react'

import { handleMessageEvent } from './utils'
import useFetchSalesforceLead from './useSalesforceFetchLead'
import useGetTreatment from 'hooks/treatments/useGetTreatment'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import { isServer } from 'utils/environment'

export type UseSalesforceLeadReturn = Partial<UseQueryResult> & {
  lead: SfLead
  hasLeadId: boolean
  leadId: string
  patients: PatientDetails[]
  isSalesForceLeadFetchEnabled: boolean
  caller?: PatientDetails
  leadResponsibleParty?: PatientDetails
  results?: SalesForceLeadResults
  isEmbeddedInSf?: boolean
}
export type UseSalesforceLeadOptions = {
  initialSfId?: string
  initialSfObjectType?: string
  ignoreExistingLeadId?: boolean
  initialLead?: SfLead
}

const useSalesforceLead = (
  options: UseSalesforceLeadOptions = {
    ignoreExistingLeadId: false,
  }
): UseSalesforceLeadReturn => {
  const router = useRouter()
  const dispatch = useDispatch()
  const { query } = router
  const { salesforceDetails } = useAppSelector((state) => state.global)

  const sfIdFromQuery = query?.sfId as string
  const sfObjectTypeFromQuery = query?.sfObjectType as string

  //TODO: Remove after THRIV-7030 is merged
  const leadIdFromQuery = query?.leadId as string
  const isSalesForceLeadFetchEnabled =
    useGetTreatment(TreatmentFlags.SALESFORCE_FETCH_LEAD_DETAILS) ===
    treatmentFlagValues.ON

  const isSalesForceB2BOn =
    useGetTreatment(TreatmentFlags.SALESFORCE_B2B_CASE_ACCOUNT) ===
    treatmentFlagValues.ON

  options = isSalesForceB2BOn
    ? {
        ...options,
        initialSfId: sfIdFromQuery || options.initialSfId,
        initialSfObjectType:
          sfObjectTypeFromQuery || options.initialSfObjectType,
      }
    : options

  const storedLeadId = isSalesForceB2BOn
    ? options.initialSfId ?? salesforceDetails?.sfId
    : options.ignoreExistingLeadId
    ? leadIdFromQuery
    : salesforceDetails?.leadId ?? leadIdFromQuery

  const validStoredLeadId =
    storedLeadId && storedLeadId !== 'undefined' ? storedLeadId : ''

  const storedObjectType =
    options.initialSfObjectType ?? salesforceDetails?.sfObjectType

  const hasLeadId = isSalesForceB2BOn
    ? Boolean(storedLeadId) && storedObjectType === 'Lead'
    : Boolean(storedLeadId)

  function isEmbeddedInSf(): boolean {
    let isEmbeddedInIframe = false

    if (!isServer()) {
      try {
        isEmbeddedInIframe = window.location !== window.parent.location
      } catch {
        isEmbeddedInIframe = true
      }
    }

    const hasSfDetails = Boolean(
      salesforceDetails?.sfObjectType && salesforceDetails?.sfId
    )
    const hasQueryParams = Boolean(sfIdFromQuery)

    return isEmbeddedInIframe || hasSfDetails || hasQueryParams
  }

  const shouldFetchLeadDetails = isSalesForceB2BOn
    ? hasLeadId
    : (hasLeadId && isSalesForceLeadFetchEnabled) || !!options.initialLead

  //TODO: Remove after THRIV-7030 is merged
  useEffect(() => {
    if (!isSalesForceB2BOn) {
      const isSkipDispatch =
        !leadIdFromQuery ||
        leadIdFromQuery === salesforceDetails?.leadId ||
        isServer()

      if (isSkipDispatch) {
        return
      }

      dispatch(setSalesforceDetails({ leadId: leadIdFromQuery }))
    }
  }, [leadIdFromQuery, salesforceDetails?.leadId, isSalesForceB2BOn])

  useEffect(() => {
    if (isSalesForceB2BOn) {
      // Determine the effective Salesforce ID and type to use
      const effectiveSfId =
        sfIdFromQuery || options.initialSfId || salesforceDetails?.sfId
      const effectiveSfObjectType =
        sfObjectTypeFromQuery ||
        options.initialSfObjectType ||
        salesforceDetails?.sfObjectType

      // Check if there's a need to update the Redux store
      const shouldUpdateRedux =
        effectiveSfId !== salesforceDetails?.sfId ||
        effectiveSfObjectType !== salesforceDetails?.sfObjectType

      if (shouldUpdateRedux && !isServer()) {
        dispatch(
          setSalesforceDetails({
            sfId: effectiveSfId,
            sfObjectType: effectiveSfObjectType,
          })
        )
      }
    }
  }, [
    sfIdFromQuery,
    sfObjectTypeFromQuery,
    options.initialSfId,
    options.initialSfObjectType,
    salesforceDetails,
    isSalesForceB2BOn,
  ])

  const leadQueryData = useFetchSalesforceLead(
    validStoredLeadId,
    shouldFetchLeadDetails
  )

  const handleLeadUpdateEvent = (event) =>
    handleMessageEvent(
      event,
      leadQueryData,
      dispatch,
      'lead',
      isSalesForceB2BOn
    )

  useEffect(() => {
    if (isServer()) {
      return
    }

    window.addEventListener('message', handleLeadUpdateEvent)
    return () => window.removeEventListener('message', handleLeadUpdateEvent)
  }, [leadQueryData, dispatch])

  if (!shouldFetchLeadDetails || !leadQueryData?.data) {
    return {
      lead: null,
      hasLeadId: false,
      leadId: '',
      patients: [],
      isSalesForceLeadFetchEnabled: false,
      ...(isSalesForceB2BOn && {
        isEmbeddedInSf: isEmbeddedInSf(),
        ...leadQueryData,
      }),
    }
  }

  const {
    patients,
    caller,
    leadResponsibleParty,
  } = leadQueryData.data.leadPatients

  return {
    lead: leadQueryData.data.lead,
    results: leadQueryData.data,
    leadId: validStoredLeadId,
    patients,
    caller,
    leadResponsibleParty,
    hasLeadId,
    ...(isSalesForceB2BOn && {
      isEmbeddedInSf: isEmbeddedInSf(),
    }),
    ...leadQueryData,
    isSalesForceLeadFetchEnabled: shouldFetchLeadDetails,
  }
}

export default useSalesforceLead
